import { useEffect, useState, type ReactNode } from 'react';

interface ToastEventDetail {
  readonly message: string;
}

const EVENT = 'miniapp:toast';

// A tiny window-event bus avoids threading a Provider through every
// route — any module can call showToast() without coupling to React.
export const showToast = (message: string): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<ToastEventDetail>(EVENT, { detail: { message } }));
};

export const Toast = (): ReactNode => {
  const [message, setMessage] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const onToast = (e: Event): void => {
      const detail = (e as CustomEvent<ToastEventDetail>).detail;
      setMessage(detail.message);
      setVisible(true);
    };
    window.addEventListener(EVENT, onToast);
    return (): void => {
      window.removeEventListener(EVENT, onToast);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = window.setTimeout(() => {
      setVisible(false);
    }, 2600);
    return (): void => {
      window.clearTimeout(t);
    };
  }, [visible, message]);

  return (
    <div
      className={`toast${visible ? ' show' : ''}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {message}
    </div>
  );
};
